import shaderSources from "@/layers/shaders/all";
import type { BinaryColormapRenderInfo } from "./BinaryColormapState";

export type BinaryMainpassDrawOptions = {
  prepassTexture: WebGLTexture;
  overlayPrepassTexture: WebGLTexture;
  opacity: number;
  colorMapName: string;
  parameterUnit: string;
  geoMinMax?: { min: number; max: number };
  colormapInfo: BinaryColormapRenderInfo;
  debugLoggingEnabled: boolean;
};

export class BinaryMainpassRenderer {
  private program?: WebGLProgram;
  private posBuffer?: WebGLBuffer;
  private uvBuffer?: WebGLBuffer;
  private aPos = -1;
  private aUv = -1;
  private uSampler?: WebGLUniformLocation;
  private uOverlaySampler?: WebGLUniformLocation;
  private uOpacity?: WebGLUniformLocation;
  private uMode?: WebGLUniformLocation;
  private uColormapSampler?: WebGLUniformLocation;
  private uProps2?: WebGLUniformLocation;

  private lastMainpassMode?: number;
  private lastColormapDebugKey?: string;

  init(gl: WebGL2RenderingContext): void {
    const vsSource = shaderSources("./binary/mainpass.vert").default;

    const fsSource = shaderSources("./binary/mainpass.frag").default;

    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type);
      if (!sh) {
        throw new Error("Failed to create shader");
      }
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(sh) || "";
        gl.deleteShader(sh);
        throw new Error(log);
      }
      return sh;
    };

    const vs = compile(gl.VERTEX_SHADER, vsSource);
    const fs = compile(gl.FRAGMENT_SHADER, fsSource);

    const program = gl.createProgram();
    if (!program) {
      throw new Error("Failed to create program");
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) || "";
      gl.deleteProgram(program);
      throw new Error(log);
    }

    this.program = program;
    this.aPos = gl.getAttribLocation(program, "a_position");
    this.aUv = gl.getAttribLocation(program, "a_texCoord");
    this.uSampler = gl.getUniformLocation(program, "u_prepass") || undefined;
    this.uOverlaySampler = gl.getUniformLocation(program, "u_overlayPrepass") || undefined;
    this.uOpacity = gl.getUniformLocation(program, "u_opacity") || undefined;
    this.uMode = gl.getUniformLocation(program, "u_mode") || undefined;
    this.uColormapSampler = gl.getUniformLocation(program, "u_colormapSampler") || undefined;
    this.uProps2 = gl.getUniformLocation(program, "u_props2") || undefined;

    const pos = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    const uv = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);

    this.posBuffer = gl.createBuffer() || undefined;
    if (this.posBuffer) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, pos, gl.STATIC_DRAW);
    }

    this.uvBuffer = gl.createBuffer() || undefined;
    if (this.uvBuffer) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, uv, gl.STATIC_DRAW);
    }
  }

  dispose(gl: WebGL2RenderingContext): void {
    if (this.program) {
      gl.deleteProgram(this.program);
    }
    if (this.posBuffer) {
      gl.deleteBuffer(this.posBuffer);
    }
    if (this.uvBuffer) {
      gl.deleteBuffer(this.uvBuffer);
    }
    this.program = undefined;
    this.posBuffer = undefined;
    this.uvBuffer = undefined;
  }

  draw(gl: WebGL2RenderingContext, options: BinaryMainpassDrawOptions): void {
    if (!this.program || !this.posBuffer || !this.uvBuffer) {
      return;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);

    gl.useProgram(this.program);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, options.prepassTexture);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, options.overlayPrepassTexture);

    if (this.uSampler) {
      gl.uniform1i(this.uSampler, 0);
    }
    if (this.uOverlaySampler) {
      gl.uniform1i(this.uOverlaySampler, 1);
    }
    if (this.uOpacity) {
      gl.uniform1f(this.uOpacity, options.opacity);
    }

    let mode = 1;
    let lower = options.geoMinMax?.min ?? 0;
    let upper = options.geoMinMax?.max ?? 1;
    let sampleCount = 256;

    const { mapping, usedStale, requestedId, syncState } = options.colormapInfo;

    if (mapping) {
      lower = mapping.recommendedValueRange.lowerBound;
      upper = mapping.recommendedValueRange.upperBound;
      sampleCount = mapping.texture.sampleCount;

      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, mapping.texture.texture(gl));
      if (this.uColormapSampler) {
        gl.uniform1i(this.uColormapSampler, 2);
      }
      mode = 2;

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, options.prepassTexture);
      if (this.uSampler) {
        gl.uniform1i(this.uSampler, 0);
      }
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, options.overlayPrepassTexture);
      if (this.uOverlaySampler) {
        gl.uniform1i(this.uOverlaySampler, 1);
      }
    } else {
      const name = (options.colorMapName || "").toLowerCase();
      mode = name.includes("gray") || name.includes("grey") || name.includes("mono") ? 0 : 1;
    }

    if (this.uProps2) {
      gl.uniform4f(this.uProps2, lower, upper, sampleCount, 0.0);
    }
    if (this.uMode) {
      gl.uniform1i(this.uMode, mode);
    }

    if (options.debugLoggingEnabled) {
      const debugKey = `${options.parameterUnit}|${options.colorMapName}`;
      if (this.lastMainpassMode !== mode || this.lastColormapDebugKey !== debugKey) {
        this.lastMainpassMode = mode;
        this.lastColormapDebugKey = debugKey;

        const modeLabel =
          mode === 2 ? (usedStale ? "LUT(stale)" : "LUT") : mode === 0 ? "fallback:grayscale" : "fallback:turbo";
        // eslint-disable-next-line no-console
        console.debug(
          `[BinaryColormap] mode=${mode} (${modeLabel}) parameter=${options.parameterUnit} colormap=${
            options.colorMapName
          } colormapState=${String(syncState)} requestedId=${requestedId}`,
        );
      }
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
    gl.enableVertexAttribArray(this.aPos);
    gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
    gl.enableVertexAttribArray(this.aUv);
    gl.vertexAttribPointer(this.aUv, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    // The LUT texture is premultiplied, so the composite pass must blend as premultiplied alpha.
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.disableVertexAttribArray(this.aPos);
    gl.disableVertexAttribArray(this.aUv);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
}
